import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { MDBBtn, MDBIcon } from "mdb-react-ui-kit";
import { DeleteConfirmModal } from "../../../components/modals/DeleteConfirmModal";
import { DataTable, DataTableStringComparison } from "../../../components/DataTable";
import { AdminPage } from "../AdminPage";
import { AddSharedGroupModal } from "./AddSharedGroupModal";
import { SplitScreenDiv } from "../../../components/SplitScreenDiv";
import { AddGroupModal } from "./AddGroupModal";
import { GroupEditor } from "./GroupEditor";

export const GroupsPage = ({canAdd, canEdit, canDelete}) => {
    const [groups, setGroups] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState<number|null>(null);
    const [addSharedGroup, setAddSharedGroup] = useState(false);
    const [addGroup, setAddGroup] = useState(false);
    const [deleteId, setDeleteId] = useState<number|null>(null);
    const { t } = useTranslation();

    const fetch = useCallback(() => {
        setLoading(true);

        API.GET('/api/admgroups')
            .then(g => { setGroups(g); setLoading(false);})
    }, []);

    useEffect(() => {
        fetch();
    }, []);

    const Table = useMemo(() => (
        <DataTable
            data={groups}
            height={500}
            onRowSelected={g => setSelectedId(g.id)}
            rowClassname={g => g.gType == 1 ? 'bg-warning bg-opacity-25' : ''}
            columns={[
                { headerName: t('name'), flex: 4, valueGetter: ({data}) => ({Name: data.Name, gType: data.gType, shared: data.shared, groupCompany: data.groupCompany}),
                    cellRenderer: ({value}) => (
                        <span>
                            <MDBIcon icon="layer-group" color="primary" className="me-1"/>
                            {value.gType == 1 && <MDBIcon icon="bookmark" color="warning" className="me-1"/>}
                            <span className={value.gType == 1?"fst-italic":""}>{value.Name}</span>
                            {value.shared &&
                                <span className="badge badge-warning ms-1">
                                    <MDBIcon icon="retweet" color="warning" className="me-1"/>
                                    <span>{value.groupCompany?.name}</span>
                                </span> 
                                // <span className="me-1" title={"Shared by " + value.groupCompany?.name}>
                                //     <MDBIcon icon="retweet" size="lg" color="warning"/>
                                // </span>
                            }
                        </span>
                    ),
                    comparator: (row1, row2) => DataTableStringComparison(row1.data.Name, row2.data.Name)
                },
                { headerName: t('locations'), field: 'nrOfLocations', flex: 2},
                { headerName: t('users'), field: 'nrOfUsers', flex: 2 }
            ]}
        />
    ), [groups]);

    const GroupEditorScreen = useMemo(() => selectedId == null ? null : (
        <GroupEditor
            groupId={selectedId}
            onClose={() => setSelectedId(null)}
            canEdit={canEdit}
            canDelete={canDelete}
            onLocationAdd={fetch}
            onLocationRemove={fetch}
            onUserAdd={() => setGroups(old => old.map(g => g.id !== selectedId ? g : ({...g, nrOfUsers: g.nrOfUsers+1})))}
            onUserRemove={() => setGroups(old => old.map(g => g.id !== selectedId ? g : ({...g, nrOfUsers: g.nrOfUsers-1})))}
            onGroupDelete={() => setDeleteId(selectedId)}
            onGroupEdit={() => fetch()}
        />
    ), [selectedId, canEdit, canDelete, fetch]);

    return (
        <AdminPage
            title={t('groups')}
            header={(<>
                <MDBBtn color="link" onClick={() => setAddSharedGroup(true)}>
                    <MDBIcon icon="right-to-bracket" className="me-1"/>
                    {t('import_group')}
                </MDBBtn>
                <MDBBtn color="link" onClick={() => setAddGroup(true)}>
                    <MDBIcon icon="plus-circle" className="me-1"/>
                    {t('add_group')}
                </MDBBtn>
            </>)}
        >
            <DeleteConfirmModal
                open={deleteId !== null}
                title={t('remove_group')}
                message={t('remove_group_prompt')}
                onCancel={() => setDeleteId(null)}
                onConfirm={() => {
                    API.DELETE('/api/admgroups/'+deleteId).then(() => { fetch(); setSelectedId(null);});
                    setDeleteId(null);
                }}
            />
            <AddSharedGroupModal
                open={addSharedGroup}
                onClose={() => setAddSharedGroup(false)}
                onJoin={id => {fetch(); setSelectedId(id);}}
            />
            <AddGroupModal
                open={addGroup}
                onClose={() => setAddGroup(false)}
                onAdd={fetch}
            />
            <SplitScreenDiv
                ratio={40}
                left={Table}
                right={GroupEditorScreen}
            />
        </AdminPage>
    )
}